import CollapsibleCard from '@/components/shared/CollapsibleCard'
import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import OrderStatusBadge from '@/components/shared/OrderStatusBadge'
import { formatFrequency } from '@/lib/subscriptions'
import { formatDateTime } from '@/lib/utils'
import type { SubscriptionListItem } from '@/services/subscriptions'
import { colors, sxPresets } from '@/theme'
import { ArrowForward } from '@mui/icons-material'
import { Box, Chip, Paper, Typography } from '@mui/material'

interface UserSubscriptionsSectionProps {
  subscriptions: SubscriptionListItem[]
  isLoading?: boolean
  onItemClick?: (hash: string) => void
}

export default function UserSubscriptionsSection({
  subscriptions,
  isLoading = false,
  onItemClick
}: UserSubscriptionsSectionProps) {
  const isEmpty = !subscriptions.length

  const loadingContent = (
    <Box sx={{ ...sxPresets().section.content, p: 4, textAlign: 'center' }}>
      <InlineLoadingSpinner text='Loading subscriptions...' />
    </Box>
  )

  return (
    <CollapsibleCard
      sectionId='subscriptions'
      title='Subscription'
      isEmpty={isEmpty || isLoading}
      emptyMessage='No subscriptions found'
      emptyContent={isLoading ? loadingContent : undefined}
    >
      <Box
        sx={{
          ...sxPresets().section.content,
          bgcolor: colors.background.surface,
          ...sxPresets().grid.threeColumn,
          ...sxPresets(200).scrollable.twoRows
        }}
      >
        {subscriptions.map(subscription => (
          <Paper
            key={subscription.hash}
            onClick={() => onItemClick?.(subscription.hash)}
            elevation={0}
            sx={{
              ...sxPresets().card.default,
              ...(onItemClick ? sxPresets().card.clickable : {}),
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1.5 }}>
              <Typography variant='smallText'>{subscription.category ?? 'Unknown'}</Typography>
              <OrderStatusBadge status={subscription.status} />
            </Box>

            <Box sx={{ px: 2, pb: 2, flex: 1 }}>
              <Typography variant='cardTitle' sx={{ mb: 1 }}>
                {subscription.planName ?? 'Subscription'}
              </Typography>
              <Typography variant='smallText' sx={{ mb: 0.5 }}>
                {subscription.subscriptionNumber ?? 'N/A'}
              </Typography>
              <Typography variant='smallText' sx={{ mb: 0.5 }}>
                Started {formatDateTime(new Date(subscription.startedAt))}
              </Typography>
              {formatFrequency(subscription) && (
                <Chip
                  label={formatFrequency(subscription)}
                  size='small'
                  sx={{
                    mt: 1,
                    color: '#4E545E',
                    borderColor: '#4E545E',
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    border: '1px solid'
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 1.5,
                bgcolor: colors.brand.primaryLight
              }}
            >
              <Typography variant='link' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                View subscription
                <ArrowForward sx={{ fontSize: '14px' }} />
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </CollapsibleCard>
  )
}
